import React, { useState, useEffect, Fragment } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  useColorModeValue,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  useToast,
  VStack,
  HStack,
  Spinner,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useDisclosure,
  IconButton,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Avatar,
  AvatarBadge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Pagination,
  usePagination,
} from "@chakra-ui/react";
import {
  SearchIcon,
  AddIcon,
  EditIcon,
  DeleteIcon,
  FilterIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";
import { PersonIcon } from "components/Icons/Icons.js";
import Card from "components/Card/Card.js";
import { usersService, citiesService, rolesService } from "api";

// Helper functions for role and text display
const getRoleDisplayText = (role) => {
  switch (role) {
    case 'admin':
      return 'مدير';
    case 'company':
      return 'شركة';
    case 'client':
      return 'عميل';
    case 'employee':
      return 'موظف';
    case 'employees':
      return 'موظفين';
    default:
      return role;
  }
};

const getGenderText = (gender) => {
  switch (gender) {
    case 'male':
      return 'ذكر';
    case 'female':
      return 'أنثى';
    default:
      return gender;
  }
};

const getLangText = (lang) => {
  switch (lang) {
    case 'ar':
      return 'العربية';
    case 'en':
      return 'English';
    default:
      return lang;
  }
};

// Generate consistent colors for roles based on role name hash
const getRoleColor = (roleName) => {
  const colors = [
    { bg: 'red.50', text: 'red.700', border: 'red.200' },
    { bg: 'blue.50', text: 'blue.700', border: 'blue.200' },
    { bg: 'green.50', text: 'green.700', border: 'green.200' },
    { bg: 'purple.50', text: 'purple.700', border: 'purple.200' },
    { bg: 'orange.50', text: 'orange.700', border: 'orange.200' },
    { bg: 'teal.50', text: 'teal.700', border: 'teal.200' },
    { bg: 'pink.50', text: 'pink.700', border: 'pink.200' },
    { bg: 'indigo.50', text: 'indigo.700', border: 'indigo.200' },
    { bg: 'yellow.50', text: 'yellow.700', border: 'yellow.200' },
    { bg: 'cyan.50', text: 'cyan.700', border: 'cyan.200' },
    { bg: 'lime.50', text: 'lime.700', border: 'lime.200' },
    { bg: 'amber.50', text: 'amber.700', border: 'amber.200' },
    { bg: 'emerald.50', text: 'emerald.700', border: 'emerald.200' },
    { bg: 'violet.50', text: 'violet.700', border: 'violet.200' },
    { bg: 'rose.50', text: 'rose.700', border: 'rose.200' },
    { bg: 'sky.50', text: 'sky.700', border: 'sky.200' }
  ];
  
  // Create a simple hash from role name to get consistent color
  let hash = 0;
  for (let i = 0; i < roleName.length; i++) {
    hash = ((hash << 5) - hash + roleName.charCodeAt(i)) & 0xffffffff;
  }
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

const getRoleBadgeBg = (role) => {
  return getRoleColor(role).bg;
};

const getRoleBadgeTextColor = (role) => {
  return getRoleColor(role).text;
};

const getRoleBadgeBorderColor = (role) => {
  return getRoleColor(role).border;
};

// Helper function for role text display
const getRoleText = (roles) => {
  if (!roles || roles.length === 0) return 'غير محدد';
  
  // Handle comma-separated string like "client, company"
  if (typeof roles === 'string' && roles.includes(',')) {
    const rolesArray = roles.split(',').map(role => role.trim());
    return rolesArray.map(role => getRoleDisplayText(role)).join(' , ');
  }
  
  // If it's a string (single role), convert to array
  const rolesArray = Array.isArray(roles) ? roles : [roles];
  
  // If multiple roles, show all of them
  if (rolesArray.length > 1) {
    return rolesArray.map(role => getRoleDisplayText(role)).join(' , ');
  }
  
  return getRoleDisplayText(rolesArray[0]);
};

// User Modal Component
function UserModal({ isOpen, onClose, user, roles, isLoadingRoles, onSubmit, actionLoading, validationErrors }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    city_id: "",
    address: "",
    gender: "",
    lang: "",
    birth_date: "",
    password: "",
    roles: [],
  });
  const [cities, setCities] = useState([]);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const toast = useToast();

  // Fetch cities for filter dropdown
  const fetchCitiesForFilter = async () => {
    setIsLoadingCities(true);
    try {
      const response = await citiesService.getCities();
      if (response.success) {
        const citiesData = response.data?.data || response.data || [];
        setCities(citiesData);
      }
    } catch (error) {
      console.error('Error fetching cities for filter:', error);
    } finally {
      setIsLoadingCities(false);
    }
  };

  // Fetch cities for dropdown
  const fetchCities = async () => {
    setIsLoadingCities(true);
    try {
      const response = await citiesService.getCities();
      if (response.success) {
        setCities(response.data?.data || response.data || []);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء جلب المدن",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingCities(false);
    }
  };



  useEffect(() => {
    if (isOpen) {
      fetchCities();
    }
  }, [isOpen]);



  // Debug: Log when roles change
  useEffect(() => {
    console.log('Roles state changed:', roles);
  }, [roles]);

  useEffect(() => {
    console.log('UserModal useEffect - user data:', user);
    console.log('UserModal useEffect - roles data:', roles);
    if (user?.id) {
      // Editing existing user
      const formDataToSet = {
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        city_id: user.city_id || "",
        address: user.address || "",
        gender: user.gender || "",
        lang: user.lang || "",
        birth_date: user.birth_date ? user.birth_date.split('T')[0] : "",
        password: "", // Don't show password when editing
        roles: Array.isArray(user.roles) ? user.roles : (user.roles ? (user.roles.includes(',') ? user.roles.split(',').map(role => role.trim()) : [user.roles]) : []),
      };
      console.log('Setting form data for editing:', formDataToSet);
      console.log('Form data state after set:', formData);
      setFormData(formDataToSet);
    } else {
      // Creating new user
      const formDataToSet = {
        name: "",
        email: "",
        phone: "",
        city_id: "",
        address: "",
        gender: "",
        lang: "",
        birth_date: "",
        password: "",
        roles: [],
      };
      console.log('Setting form data for new user:', formDataToSet);
      setFormData(formDataToSet);
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim()) {
      return;
    }

    // Prepare data for submission
    const submitData = { ...formData };
    
    // Convert roles array to the format expected by the API
    if (submitData.roles.length > 0) {
      submitData.roles = submitData.roles;
    }

    await onSubmit(submitData);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRoleChange = (role, isChecked) => {
    setFormData(prev => ({
      ...prev,
      roles: isChecked 
        ? [...prev.roles, role]
        : prev.roles.filter(r => r !== role)
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)">
        <ModalContent mx="20px" maxW="600px" borderRadius="15px">
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            borderBottom="1px solid"
            borderColor="gray.200"
            pb="20px"
          >
            {user?.id ? "تعديل المستخدم" : "إضافة مستخدم جديد"}
          </ModalHeader>
          <form onSubmit={handleSubmit}>
            <ModalBody pt="20px" pb="20px">
              <VStack spacing={4}>
                <FormControl isInvalid={!!validationErrors?.name}>
                  <FormLabel>الاسم</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="أدخل اسم المستخدم"
                    borderColor={validationErrors?.name ? "red.500" : "gray.300"}
                    _focus={{ borderColor: validationErrors?.name ? "red.400" : "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
                    _hover={{ borderColor: validationErrors?.name ? "red.400" : "gray.300" }}
                  />
                  <FormErrorMessage textAlign="right">
                    {validationErrors?.name?.[0]}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!validationErrors?.email}>
                  <FormLabel>البريد الإلكتروني</FormLabel>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="أدخل البريد الإلكتروني"
                    borderColor={validationErrors?.email ? "red.500" : "gray.300"}
                    _focus={{ borderColor: validationErrors?.email ? "red.400" : "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
                    _hover={{ borderColor: validationErrors?.email ? "red.400" : "gray.300" }}
                  />
                  <FormErrorMessage textAlign="right">
                    {validationErrors?.email?.[0]}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!validationErrors?.phone}>
                  <FormLabel>رقم الهاتف</FormLabel>
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="أدخل رقم الهاتف"
                    borderColor={validationErrors?.phone ? "red.500" : "gray.300"}
                    _focus={{ borderColor: validationErrors?.phone ? "red.400" : "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
                    _hover={{ borderColor: validationErrors?.phone ? "red.400" : "gray.300" }}
                  />
                  <FormErrorMessage textAlign="right">
                    {validationErrors?.phone?.[0]}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!validationErrors?.city_id}>
                  <FormLabel>المدينة</FormLabel>
                  <Select
                    value={formData.city_id}
                    onChange={(e) => handleInputChange('city_id', e.target.value)}
                    placeholder="اختر المدينة"
                    borderColor={validationErrors?.city_id ? "red.500" : "gray.300"}
                    _focus={{ borderColor: validationErrors?.city_id ? "red.400" : "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
                    _hover={{ borderColor: validationErrors?.city_id ? "red.400" : "gray.300" }}
                    isLoading={isLoadingCities}
                  >
                    {cities.map((city) => (
                      <option key={city.id} value={city.id}>
                        {city.name}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage textAlign="right">
                    {validationErrors?.city_id?.[0]}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!validationErrors?.address}>
                  <FormLabel>العنوان</FormLabel>
                  <Input
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="أدخل العنوان"
                    borderColor={validationErrors?.address ? "red.500" : "gray.300"}
                    _focus={{ borderColor: validationErrors?.address ? "red.400" : "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
                    _hover={{ borderColor: validationErrors?.address ? "red.400" : "gray.300" }}
                  />
                  <FormErrorMessage textAlign="right">
                    {validationErrors?.address?.[0]}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!validationErrors?.gender}>
                  <FormLabel>الجنس</FormLabel>
                  <Select
                    value={formData.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                    placeholder="اختر الجنس"
                    borderColor={validationErrors?.gender ? "red.500" : "gray.300"}
                    _focus={{ borderColor: validationErrors?.gender ? "red.400" : "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
                    _hover={{ borderColor: validationErrors?.gender ? "red.400" : "gray.300" }}
                  >
                    <option value="male">ذكر</option>
                    <option value="female">أنثى</option>
                  </Select>
                  <FormErrorMessage textAlign="right">
                    {validationErrors?.gender?.[0]}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!validationErrors?.lang}>
                  <FormLabel>اللغة</FormLabel>
                  <Select
                    value={formData.lang}
                    onChange={(e) => handleInputChange('lang', e.target.value)}
                    placeholder="اختر اللغة"
                    borderColor={validationErrors?.lang ? "red.500" : "gray.300"}
                    _focus={{ borderColor: validationErrors?.lang ? "red.400" : "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
                    _hover={{ borderColor: validationErrors?.lang ? "red.400" : "gray.300" }}
                  >
                    <option value="ar">العربية</option>
                    <option value="en">English</option>
                  </Select>
                  <FormErrorMessage textAlign="right">
                    {validationErrors?.lang?.[0]}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!validationErrors?.birth_date}>
                  <FormLabel>تاريخ الميلاد</FormLabel>
                  <Input
                    type="date"
                    value={formData.birth_date}
                    onChange={(e) => handleInputChange('birth_date', e.target.value)}
                    borderColor={validationErrors?.birth_date ? "red.500" : "gray.300"}
                    _focus={{ borderColor: validationErrors?.birth_date ? "red.400" : "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
                    _hover={{ borderColor: validationErrors?.birth_date ? "red.400" : "gray.300" }}
                  />
                  <FormErrorMessage textAlign="right">
                    {validationErrors?.birth_date?.[0]}
                  </FormErrorMessage>
                </FormControl>

                {!user?.id && (
                  <FormControl isInvalid={!!validationErrors?.password}>
                    <FormLabel>كلمة المرور</FormLabel>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder="أدخل كلمة المرور"
                      borderColor={validationErrors?.password ? "red.500" : "gray.300"}
                      _focus={{ borderColor: validationErrors?.password ? "red.400" : "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
                      _hover={{ borderColor: validationErrors?.password ? "red.400" : "gray.300" }}
                    />
                    <FormErrorMessage textAlign="right">
                      {validationErrors?.password?.[0]}
                    </FormErrorMessage>
                  </FormControl>
                )}

                <FormControl isInvalid={!!validationErrors?.roles}>
                  <FormLabel>الأدوار</FormLabel>
                  {isLoadingRoles ? (
                    <Flex align="center" gap={2}>
                      <Spinner size="sm" />
                      <Text fontSize="sm" color="gray.500">جاري تحميل الأدوار...</Text>
                    </Flex>
                  ) : roles.length > 0 ? (
                    <VStack align="start" spacing={2}>
                      {roles.map((role) => (
                        <HStack key={role.id}>
                          <input
                            type="checkbox"
                            id={`role-${role.name}`}
                            checked={formData.roles.includes(role.name)}
                            onChange={(e) => handleRoleChange(role.name, e.target.checked)}
                          />
                          <label htmlFor={`role-${role.name}`}>
                            {getRoleDisplayText(role.name)}
                          </label>
                        </HStack>
                      ))}
                    </VStack>
                  ) : (
                    <Text fontSize="sm" color="gray.500">لا توجد أدوار متاحة</Text>
                  )}
                  <FormErrorMessage textAlign="right">
                    {validationErrors?.roles?.[0]}
                  </FormErrorMessage>
                  {console.log('Roles in UserModal:', roles, 'Length:', roles.length)}
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter
              borderTop="1px solid"
              borderColor="gray.200"
              pt="20px"
              gap="15px"
            >
              <Button
                onClick={onClose}
                size="lg"
                minW="120px"
                borderRadius="8px"
                variant="outline"
                border="2px solid"
                borderColor="gray.300"
                _hover={{ bg: "gray.50", borderColor: "gray.400" }}
              >
                إلغاء
              </Button>
              <Button
                colorScheme="blue"
                type="submit"
                size="lg"
                minW="120px"
                borderRadius="8px"
                isLoading={actionLoading.create || actionLoading.update}
                loadingText={user?.id ? "جاري التحديث..." : "جاري الإنشاء..."}
                _hover={{ transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)" }}
              >
                {user?.id ? "تحديث" : "إنشاء"}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
}

// Main Users Component
export default function Users() {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPagination, setIsLoadingPagination] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    city_id: "",
    gender: "",
    roles: [],
    lang: "",
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isReadyToOpenModal, setIsReadyToOpenModal] = useState(false);
  const [actionLoading, setActionLoading] = useState({
    create: false,
    update: false,
    delete: false,
    edit: false,
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [cities, setCities] = useState([]);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [roles, setRoles] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Debug: Log when editingUser changes
  useEffect(() => {
    console.log('editingUser state changed:', editingUser);
  }, [editingUser]);

  // Open modal when ready
  useEffect(() => {
    if (isReadyToOpenModal && editingUser) {
      console.log('Ready to open modal, opening now');
      setIsModalOpen(true);
      setIsReadyToOpenModal(false);
    }
  }, [isReadyToOpenModal, editingUser]);

  const toast = useToast();
  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const cardBg = useColorModeValue("white", "navy.700");

  // Fetch users
  const fetchUsers = async (page = 1) => {
    setIsLoading(true);
    setApiError(null);
    try {
      const params = {
        page,
        per_page: pagination.per_page,
        ...filters,
      };
      
      // Remove empty roles array if no roles are selected
      if (params.roles && params.roles.length === 0) {
        delete params.roles;
      }
      
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      console.log('Fetching users with params:', params);
      const response = await usersService.getUsers(params);
      console.log('Users response:', response);
      console.log('Response success:', response.success);
      console.log('Response status:', response.status);
      console.log('Response data:', response.data);
      
      // Check for both success (boolean) and status (number) fields
      if ((response.success || response.status === 1) && response.data) {
        console.log('Processing response data:', response.data);
        
        // Handle the API response structure: { data: { data: [...], current_page: 1, ... } }
        let usersData = [];
        let paginationData = {};

        // The API response structure is: response.data.data (nested data)
        if (response.data.data.data && Array.isArray(response.data.data.data)) {
          // Standard API response structure
          usersData = response.data.data.data;
          console.log('Extracted users from response.data.data.data:', usersData);
          paginationData = {
            current_page: response.data.data.current_page || 1,
            per_page: response.data.data.per_page || 15,
            total: response.data.data.total || 0,
            last_page: response.data.data.last_page || 1,
          };
          console.log('Extracted users from response.data.data:', usersData);
          console.log('Extracted pagination from response.data:', paginationData);
        } else if (Array.isArray(response.data)) {
          // Direct array response
          usersData = response.data;
          paginationData = {
            current_page: 1,
            per_page: response.data.length,
            total: response.data.length,
            last_page: 1,
          };
          console.log('Extracted users from direct response.data:', usersData);
        } else {
          // Fallback: empty array
          usersData = [];
          paginationData = {
            current_page: 1,
            per_page: 15,
            total: 0,
            last_page: 1,
          };
          console.log('No users data found, using fallback');
        }
        
        setUsers(usersData);
        setPagination(paginationData);
        
        console.log('Users data set:', usersData);
        console.log('Pagination set:', paginationData);
        console.log('Users state after set:', usersData);
      } else {
        setUsers([]);
        setApiError(response.message || 'حدث خطأ أثناء جلب المستخدمين');
        toast({
          title: "خطأ",
          description: response.message || "حدث خطأ أثناء جلب المستخدمين",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error in fetchUsers:', error);
      setUsers([]);
      setApiError('حدث خطأ غير متوقع');
      toast({
        title: "خطأ",
        description: "حدث خطأ غير متوقع",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch cities for filter dropdown
  const fetchCitiesForFilter = async () => {
    setIsLoadingCities(true);
    try {
      const response = await citiesService.getCities();
      if (response.success) {
        const citiesData = response.data?.data || response.data || [];
        setCities(citiesData);
      }
    } catch (error) {
      console.error('Error fetching cities for filter:', error);
    } finally {
      setIsLoadingCities(false);
    }
  };

  // Fetch roles for filters and form
  const fetchRoles = async () => {
    setIsLoadingRoles(true);
    try {
      console.log('Fetching roles...');
      const response = await rolesService.getRoles();
      console.log('Roles response:', response);
      
      if (response.success || response.status === 1) {
        // Handle different response structures
        let rolesData = [];
        if (response.data && Array.isArray(response.data)) {
          // Direct array response
          rolesData = response.data;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          // Nested data response
          rolesData = response.data.data;
        }
        
        console.log('Extracted roles data:', rolesData);
        setRoles(rolesData);
      } else {
        console.log('Roles response not successful:', response);
        toast({
          title: "تحذير",
          description: "لم يتم جلب الأدوار بنجاح",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء جلب الأدوار",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingRoles(false);
    }
  };

  useEffect(() => {
    // Initialize with empty arrays to prevent errors
    setUsers([]);
    setCities([]);
    
    // Fetch data
    fetchUsers();
    fetchCitiesForFilter();
    fetchRoles();
  }, []);

  // Handle search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchUsers(1);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, filters]);

  // Handle page change
  const handlePageChange = async (newPage) => {
    if (newPage >= 1 && newPage <= pagination.last_page) {
      setIsLoadingPagination(true);
      try {
        await fetchUsers(newPage);
      } finally {
        setIsLoadingPagination(false);
      }
    }
  };

  // Modal handlers
  const openCreateModal = () => {
    setEditingUser(null);
    setValidationErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = async (user) => {
    setActionLoading({ edit: true });
    try {
      console.log('Opening edit modal for user:', user);
      
      // Ensure cities are loaded first
      if (cities.length === 0) {
        console.log('Cities not loaded, fetching...');
        await fetchCities();
        console.log('Cities loaded:', cities);
      } else {
        console.log('Cities already loaded:', cities);
      }
      
      // Fetch fresh user data from API
      const response = await usersService.getUserById(user.id);
      console.log('API response for user:', response);
      if (response.success || response.status === 1) {
        console.log('Setting editing user data:', response.data);
        
        // Extract user data from response.data.data
        let userData = response.data.data;
        console.log('Extracted user data:', userData);
        
        // Map city name to city_id if cities are available
        console.log('Cities available:', cities);
        console.log('User city:', userData.city);
        if (cities.length > 0 && userData.city) {
          const cityObj = cities.find(city => city.name === userData.city);
          console.log('Found city object:', cityObj);
          if (cityObj) {
            userData = { ...userData, city_id: cityObj.id };
            console.log('Added city_id:', userData.city_id);
          }
        } else {
          console.log('No cities available or no city name');
        }
        
        console.log('Final userData after mapping:', userData);
        setEditingUser(userData);
        console.log('editingUser state after set:', editingUser);
        setValidationErrors({});
        // Mark as ready to open modal
        setIsReadyToOpenModal(true);
      } else {
        toast({
          title: "خطأ",
          description: response.message || "حدث خطأ أثناء جلب بيانات المستخدم",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error in openEditModal:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء جلب بيانات المستخدم",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading({ edit: false });
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    setValidationErrors({});
    setIsReadyToOpenModal(false);
  };

  // Delete handlers
  const openDeleteDialog = (user) => {
    setDeletingUser(user);
    setIsDeleteOpen(true);
  };

  const onDeleteClose = () => {
    setIsDeleteOpen(false);
    setDeletingUser(null);
  };

  // CRUD operations
  const handleCreateUser = async (userData) => {
    setActionLoading({ create: true });
    try {
      const response = await usersService.createUser(userData);
      if (response.success) {
        toast({
          title: "نجح",
          description: "تم إنشاء المستخدم بنجاح",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        handleModalClose();
        fetchUsers(pagination.current_page);
      } else {
        if (response.status === 422 && response.data?.data?.error_messages) {
          setValidationErrors(response.data.data.error_messages);
        } else {
          toast({
            title: "خطأ",
            description: response.message || "حدث خطأ أثناء إنشاء المستخدم",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error('Error in handleCreateUser:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ غير متوقع",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading({ create: false });
    }
  };

  const handleUpdateUser = async (userData) => {
    setActionLoading({ update: true });
    try {
      const response = await usersService.updateUser(editingUser.id, userData);
      if (response.success) {
        toast({
          title: "نجح",
          description: "تم تحديث المستخدم بنجاح",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        handleModalClose();
        fetchUsers(pagination.current_page);
      } else {
        if (response.status === 422 && response.data?.data?.error_messages) {
          setValidationErrors(response.data.data.error_messages);
        } else {
          toast({
            title: "خطأ",
            description: response.message || "حدث خطأ أثناء تحديث المستخدم",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error('Error in handleUpdateUser:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ غير متوقع",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading({ update: false });
    }
  };

  const handleDeleteUser = async (id) => {
    setActionLoading({ delete: true });
    try {
      const response = await usersService.deleteUser(id);
      if (response.success) {
        toast({
          title: "نجح",
          description: "تم حذف المستخدم بنجاح",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onDeleteClose();
        fetchUsers(pagination.current_page);
      } else {
        toast({
          title: "خطأ",
          description: response.message || "حدث خطأ أثناء حذف المستخدم",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error in handleDeleteUser:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ غير متوقع",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading({ delete: false });
    }
  };

  const handleEdit = (user) => {
    openEditModal(user);
  };

  const handleDelete = (user) => {
    openDeleteDialog(user);
  };







  const getRoleBadges = (roles) => {
    if (!roles || roles.length === 0) {
      return (
        <Badge
          bg="gray.100"
          color="gray.600"
          border="1px solid"
          borderColor="gray.200"
          fontSize="xs"
          px={3}
          py={1.5}
          borderRadius="full"
          fontWeight="500"
          boxShadow="sm"
        >
          غير محدد
        </Badge>
      );
    }
    
    // Handle comma-separated string like "client, company"
    if (typeof roles === 'string' && roles.includes(',')) {
      const rolesArray = roles.split(',').map(role => role.trim());
      return rolesArray.map((role, index) => (
        <Badge 
          key={index} 
          bg={getRoleBadgeBg(role)}
          color={getRoleBadgeTextColor(role)}
          border="1px solid"
          borderColor={getRoleBadgeBorderColor(role)}
          fontSize="xs"
          px={3}
          py={1.5}
          borderRadius="full"
          fontWeight="600"
          boxShadow="0 2px 4px rgba(0,0,0,0.1)"
          _hover={{
            transform: "translateY(-1px)",
            boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
            bg: getRoleBadgeBg(role).replace('50', '100')
          }}
          transition="all 0.2s"
          cursor="default"
        >
          {getRoleDisplayText(role)}
        </Badge>
      ));
    }
    
    // If it's a string (single role), convert to array
    const rolesArray = Array.isArray(roles) ? roles : [roles];
    
    // Return array of badges for each role
          return rolesArray.map((role, index) => (
        <Badge 
          key={index} 
          bg={getRoleBadgeBg(role)}
          color={getRoleBadgeTextColor(role)}
          border="1px solid"
          borderColor={getRoleBadgeBorderColor(role)}
          fontSize="xs"
          px={3}
          py={1.5}
          borderRadius="full"
          fontWeight="600"
          boxShadow="0 2px 4px rgba(0,0,0,0.1)"
          _hover={{
            transform: "translateY(-1px)",
            boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
            bg: getRoleBadgeBg(role).replace('50', '100')
          }}
          transition="all 0.2s"
          cursor="default"
        >
          {getRoleDisplayText(role)}
        </Badge>
      ));
  };





  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card>
        <Flex direction="column" w="100%">
          <Flex
            direction={{ base: "column", md: "row" }}
            align="center"
            justify="space-between"
            mb="20px"
            gap="20px"
          >
            <Text fontSize="xl" color={textColor} fontWeight="bold">
              إدارة المستخدمين
            </Text>
            <Button
              colorScheme="blue"
              leftIcon={<AddIcon />}
              onClick={openCreateModal}
            >
              إضافة مستخدم جديد
            </Button>
          </Flex>

          {/* Search and Filters */}
          <VStack spacing={4} mb="20px" align="stretch">
            <Flex gap={2}>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="البحث في المستخدمين..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  borderColor={borderColor}
                  isDisabled={isLoading}
                />
                {isLoading && (
                  <InputRightElement>
                    <Spinner size="sm" />
                  </InputRightElement>
                )}
              </InputGroup>
              
              {searchTerm && (
                <Button
                  size="md"
                  variant="outline"
                  onClick={() => setSearchTerm("")}
                  colorScheme="gray"
                >
                  مسح البحث
                </Button>
              )}
            </Flex>

            <Flex direction={{ base: "column", md: "row" }} gap={4}>
              <Select
                value={filters.city_id}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, city_id: e.target.value }));
                  setPagination(prev => ({ ...prev, current_page: 1 }));
                }}
                placeholder={isLoadingCities ? "جاري التحميل..." : "جميع المدن"}
                borderColor={borderColor}
                minW="150px"
                isLoading={isLoadingCities}
                isDisabled={isLoadingCities}
              >
                <option value="">جميع المدن</option>
                {cities.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.name}
                  </option>
                ))}
              </Select>

              <Select
                value={filters.gender}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, gender: e.target.value }));
                  setPagination(prev => ({ ...prev, current_page: 1 }));
                }}
                placeholder="جميع الأجناس"
                borderColor={borderColor}
                minW="150px"
              >
                <option value="">جميع الأجناس</option>
                <option value="male">ذكر</option>
                <option value="female">أنثى</option>
              </Select>

              <VStack align="start" spacing={2} minW="150px">
                <Text fontSize="sm" fontWeight="medium">الأدوار</Text>
                <HStack spacing={3} flexWrap="wrap">
                  {isLoadingRoles ? (
                    <Spinner size="sm" />
                  ) : roles.length > 0 ? (
                    roles.map((role) => (
                      <HStack key={role.id}>
                        <input
                          type="checkbox"
                          id={`filter-role-${role.name}`}
                          checked={filters.roles.includes(role.name)}
                          onChange={(e) => {
                            const newRoles = e.target.checked 
                              ? [...filters.roles, role.name]
                              : filters.roles.filter(r => r !== role.name);
                            setFilters(prev => ({ ...prev, roles: newRoles }));
                            setPagination(prev => ({ ...prev, current_page: 1 }));
                          }}
                        />
                        <label htmlFor={`filter-role-${role.name}`}>
                          {getRoleDisplayText(role.name)}
                        </label>
                      </HStack>
                    ))
                  ) : (
                    <Text fontSize="xs" color="gray.500">لا توجد أدوار متاحة</Text>
                  )}
                </HStack>
                {console.log('Roles in filter:', roles, 'Length:', roles.length)}
              </VStack>

              <Select
                value={filters.lang}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, lang: e.target.value }));
                  setPagination(prev => ({ ...prev, current_page: 1 }));
                }}
                placeholder="جميع اللغات"
                borderColor={borderColor}
                minW="150px"
              >
                <option value="">جميع اللغات</option>
                <option value="ar">العربية</option>
                <option value="en">English</option>
              </Select>
            </Flex>
          </VStack>

          {/* Per Page Selector and Refresh */}
          <Flex justify="space-between" align="center" mb={4}>
            <Flex align="center" gap={2}>
              <Text fontSize="sm" color="gray.600">
                عرض
              </Text>
              <Select
                value={pagination.per_page}
                onChange={(e) => {
                  const newPerPage = parseInt(e.target.value);
                  setPagination(prev => ({ ...prev, per_page: newPerPage, current_page: 1 }));
                  fetchUsers(1);
                }}
                w="80px"
                size="sm"
              >
                <option value={15}>15</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Select>
              <Text fontSize="sm" color="gray.600">
                مستخدم في الصفحة
              </Text>
            </Flex>
            
            <HStack spacing={2}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setFilters({
                    city_id: "",
                    gender: "",
                    role: "",
                    lang: "",
                  });
                  setSearchTerm("");
                  setPagination(prev => ({ ...prev, current_page: 1 }));
                  fetchUsers(1);
                }}
                colorScheme="gray"
              >
                مسح الفلاتر
              </Button>
              
              <Button
                size="sm"
                variant="outline"
                onClick={() => fetchUsers(pagination.current_page)}
                leftIcon={<SearchIcon />}
                isLoading={isLoading}
              >
                تحديث
              </Button>
            </HStack>
          </Flex>

          {/* Results Summary and Active Filters */}
          <Flex justify="space-between" align="center" mb={4} p={3} bg="gray.50" borderRadius="md">
            <VStack align="start" spacing={1}>
              {isLoading ? (
                <Flex align="center" gap={2}>
                  <Spinner size="sm" />
                  <Text fontSize="sm" color="gray.600">جاري التحميل...</Text>
                </Flex>
              ) : (
                <Text fontSize="sm" color="gray.600">
                  عرض {((pagination.current_page - 1) * pagination.per_page) + 1} إلى {Math.min(pagination.current_page * pagination.per_page, pagination.total)} من {pagination.total} مستخدم
                </Text>
              )}
              
              {/* Active Filters Display */}
              {(filters.city_id || filters.gender || (filters.roles && filters.roles.length > 0) || filters.lang || searchTerm) && (
                <Flex gap={2} flexWrap="wrap">
                  <Text fontSize="xs" color="gray.500">الفلاتر النشطة:</Text>
                  {searchTerm && (
                    <Badge size="sm" colorScheme="blue" variant="subtle">
                      بحث: {searchTerm}
                    </Badge>
                  )}
                  {filters.city_id && cities.find(c => c.id == filters.city_id) && (
                    <Badge size="sm" colorScheme="green" variant="subtle">
                      مدينة: {cities.find(c => c.id == filters.city_id)?.name}
                    </Badge>
                  )}
                  {filters.gender && (
                    <Badge size="sm" colorScheme="purple" variant="subtle">
                      جنس: {filters.gender === 'male' ? 'ذكر' : 'أنثى'}
                    </Badge>
                  )}
                  {filters.roles && filters.roles.length > 0 && (
                    <Badge size="sm" colorScheme="orange" variant="subtle">
                      دور: {filters.roles.map(role => 
                        role === 'admin' ? 'مدير' : role === 'company' ? 'شركة' : 'عميل'
                      ).join(', ')}
                    </Badge>
                  )}
                  {filters.lang && (
                    <Badge size="sm" colorScheme="teal" variant="subtle">
                      لغة: {filters.lang === 'ar' ? 'العربية' : 'English'}
                    </Badge>
                  )}
                </Flex>
              )}
            </VStack>
            
            <Text fontSize="sm" color="gray.600">
              صفحة {pagination.current_page} من {pagination.last_page}
            </Text>
          </Flex>

          {/* Users Table */}
          <Box
            bg={cardBg}
            borderRadius="lg"
            border="1px solid"
            borderColor={borderColor}
            overflow="hidden"
          >

            {isLoading ? (
              <Flex direction="column" justify="center" align="center" p={8} gap={4}>
                <Spinner size="lg" color="blue.500" />
                <Text color="gray.600">جاري تحميل المستخدمين...</Text>
                
                {/* Skeleton Table */}
                <Box w="100%" mt={4}>
                  <TableContainer>
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>المستخدم</Th>
                          <Th>البريد الإلكتروني</Th>
                          <Th>الهاتف</Th>
                          <Th>المدينة</Th>
                          <Th>الدور</Th>
                          <Th>الجنس</Th>
                          <Th>اللغة</Th>
                          <Th>تاريخ الانضمام</Th>
                          <Th>الإجراءات</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {Array.from({ length: 5 }, (_, i) => (
                          <Tr key={i}>
                            <Td>
                              <Flex align="center" gap={3}>
                                <Box w="32px" h="32px" bg="gray.200" borderRadius="full" />
                                <Box w="120px" h="20px" bg="gray.200" borderRadius="md" />
                              </Flex>
                            </Td>
                            <Td><Box w="150px" h="20px" bg="gray.200" borderRadius="md" /></Td>
                            <Td><Box w="100px" h="20px" bg="gray.200" borderRadius="md" /></Td>
                            <Td><Box w="80px" h="20px" bg="gray.200" borderRadius="md" /></Td>
                            <Td><Box w="60px" h="20px" bg="gray.200" borderRadius="md" /></Td>
                            <Td><Box w="60px" h="20px" bg="gray.200" borderRadius="md" /></Td>
                            <Td><Box w="60px" h="20px" bg="gray.200" borderRadius="md" /></Td>
                            <Td><Box w="100px" h="20px" bg="gray.200" borderRadius="md" /></Td>
                            <Td>
                              <Flex gap={2}>
                                <Box w="32px" h="32px" bg="gray.200" borderRadius="md" />
                                <Box w="32px" h="32px" bg="gray.200" borderRadius="md" />
                              </Flex>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              </Flex>
            ) : apiError ? (
              <Flex justify="center" align="center" p={8}>
                <Text color="red.500">{apiError}</Text>
              </Flex>
            ) : !Array.isArray(users) ? (
              <Flex justify="center" align="center" p={8}>
                <Text color="red.500">خطأ في تنسيق البيانات</Text>
              </Flex>
            ) : users.length === 0 ? (
              <Flex justify="center" align="center" p={8} direction="column" gap={4}>
                <Box textAlign="center">
                  <Text color="gray.500" fontSize="xl" mb={2}>
                                        {searchTerm || filters.city_id || filters.gender || (filters.roles && filters.roles.length > 0) || filters.lang
                      ? "لا توجد نتائج تطابق الفلاتر المحددة" 
                      : "لا يوجد مستخدمين"}
                  </Text>
                  <Text color="gray.400" fontSize="sm">
                    {searchTerm || filters.city_id || filters.gender || (filters.roles && filters.roles.length > 0) || filters.lang 
                      ? "جرب تغيير الفلاتر أو البحث عن شيء آخر"
                      : "لم يتم العثور على مستخدمين في النظام"}
                  </Text>
                </Box>
                
                {(searchTerm || filters.city_id || filters.gender || (filters.roles && filters.roles.length > 0) || filters.lang) && (
                  <Button
                    size="md"
                    variant="outline"
                    onClick={() => {
                                        setFilters({
                    city_id: "",
                    gender: "",
                    roles: [],
                    lang: "",
                  });
                      setSearchTerm("");
                      setPagination(prev => ({ ...prev, current_page: 1 }));
                      fetchUsers(1);
                    }}
                    colorScheme="blue"
                    leftIcon={<SearchIcon />}
                  >
                    مسح جميع الفلاتر
                  </Button>
                )}
              </Flex>
            ) : (
              <Fragment>
                <TableContainer>
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>المستخدم</Th>
                      <Th>البريد الإلكتروني</Th>
                      <Th>الهاتف</Th>
                      <Th>المدينة</Th>
                      <Th>الدور</Th>
                      <Th>الجنس</Th>
                      <Th>اللغة</Th>
                      <Th>تاريخ الانضمام</Th>
                      <Th>الإجراءات</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {Array.isArray(users) && users.map((user) => (
                      <Tr key={user.id}>
                        <Td>
                          <Flex align="center" gap={3}>
                            <Avatar size="sm" name={user.name}>
                              <AvatarBadge boxSize="1em" bg="green.500" />
                            </Avatar>
                            <Text fontWeight="medium">{user.name}</Text>
                          </Flex>
                        </Td>
                        <Td>{user.email}</Td>
                        <Td>{user.phone || "-"}</Td>
                        <Td>{user.city}</Td>
                        <Td>
                          <HStack spacing={2} flexWrap="wrap" align="start">
                            {getRoleBadges(user.roles)}
                          </HStack>
                        </Td>
                        <Td>{getGenderText(user.gender)}</Td>
                        <Td>{getLangText(user.lang)}</Td>
                        <Td>{user.join_date}</Td>
                        <Td>
                          <HStack spacing={2}>
                            <Tooltip label="تعديل">
                              <IconButton
                                icon={<EditIcon />}
                                size="sm"
                                colorScheme="blue"
                                variant="ghost"
                                onClick={() => handleEdit(user)}
                                isLoading={actionLoading.edit}
                                isDisabled={actionLoading.edit}
                              />
                            </Tooltip>
                            <Tooltip label="حذف">
                              <IconButton
                                icon={<DeleteIcon />}
                                size="sm"
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => handleDelete(user)}
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
              
              {/* Pagination Controls */}
              {Array.isArray(users) && users.length > 0 && (
                <Flex justify="center" align="center" mt={6} gap={2} flexWrap="wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(1)}
                    isDisabled={pagination.current_page <= 1 || isLoadingPagination}
                    isLoading={isLoadingPagination && pagination.current_page === 1}
                  >
                    الأولى
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                    isDisabled={pagination.current_page <= 1 || isLoadingPagination}
                    leftIcon={<ChevronLeftIcon />}
                    isLoading={isLoadingPagination}
                  >
                    السابق
                  </Button>
                  
                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                    let pageNum;
                    if (pagination.last_page <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.current_page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.current_page >= pagination.last_page - 2) {
                      pageNum = pagination.last_page - 4 + i;
                    } else {
                      pageNum = pagination.current_page - 2 + i;
                    }
                    
                    if (pageNum < 1 || pageNum > pagination.last_page) return null;
                    
                    return (
                      <Button
                        key={pageNum}
                        size="sm"
                        variant={pagination.current_page === pageNum ? "solid" : "outline"}
                        colorScheme={pagination.current_page === pageNum ? "blue" : "gray"}
                        onClick={() => handlePageChange(pageNum)}
                        isLoading={isLoadingPagination}
                        isDisabled={isLoadingPagination}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                    isDisabled={pagination.current_page >= pagination.last_page || isLoadingPagination}
                    rightIcon={<ChevronRightIcon />}
                    isLoading={isLoadingPagination}
                  >
                    التالي
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(pagination.last_page)}
                    isDisabled={pagination.current_page >= pagination.last_page || isLoadingPagination}
                    isLoading={isLoadingPagination && pagination.current_page === pagination.last_page}
                  >
                    الأخيرة
                  </Button>
                  
                  <Text fontSize="sm" color="gray.600" ml={4}>
                    إجمالي {pagination.total} مستخدم
                  </Text>
                  
                  {/* Go to Page Input */}
                  <HStack spacing={2} ml={4}>
                    <Text fontSize="sm" color="gray.600">اذهب إلى:</Text>
                    <Input
                      size="sm"
                      w="60px"
                      type="number"
                      min={1}
                      max={pagination.last_page}
                      value={pagination.current_page}
                      onChange={(e) => {
                        const page = parseInt(e.target.value);
                        if (page >= 1 && page <= pagination.last_page) {
                          setPagination(prev => ({ ...prev, current_page: page }));
                        }
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          const page = parseInt(e.target.value);
                          if (page >= 1 && page <= pagination.last_page) {
                            handlePageChange(page);
                          }
                        }
                      }}
                    />
                    <Text fontSize="sm" color="gray.600">من {pagination.last_page}</Text>
                  </HStack>
                </Flex>
              )}
                </Fragment>
              )}
          </Box>
        </Flex>
      </Card>

      {/* Create/Edit Modal */}
      {console.log('Rendering UserModal with editingUser:', editingUser, 'isModalOpen:', isModalOpen, 'editingUser.id:', editingUser?.id)}
      <UserModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        user={editingUser}
        roles={roles}
        isLoadingRoles={isLoadingRoles}
        onSubmit={editingUser?.id ? handleUpdateUser : handleCreateUser}
        actionLoading={actionLoading}
        validationErrors={validationErrors}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered>
        <AlertDialogOverlay bg="blackAlpha.300" backdropFilter="blur(10px)">
          <AlertDialogContent
            mx="20px"
            maxW="500px"
            zIndex={9999}
            position="relative"
            borderRadius="15px"
            boxShadow="0 20px 60px rgba(0, 0, 0, 0.3)"
            border="1px solid"
            borderColor="gray.200"
          >
            <AlertDialogHeader
              fontSize="xl"
              fontWeight="bold"
              textAlign="center"
              borderBottom="1px solid"
              borderColor="gray.200"
              pb="20px"
            >
              حذف المستخدم
            </AlertDialogHeader>
            <AlertDialogBody
              pt="20px"
              pb="20px"
              textAlign="center"
              fontSize="md"
              color="gray.600"
            >
              هل أنت متأكد من حذف المستخدم <strong>"{deletingUser?.name}"</strong>؟ لا يمكن التراجع عن هذا الإجراء.
            </AlertDialogBody>
            <AlertDialogFooter
              borderTop="1px solid"
              borderColor="gray.200"
              pt="20px"
              gap="15px"
            >
              <Button
                onClick={onDeleteClose}
                size="lg"
                minW="120px"
                borderRadius="8px"
                variant="outline"
                border="2px solid"
                borderColor="gray.300"
                _hover={{ bg: "gray.50", borderColor: "gray.400" }}
              >
                إلغاء
              </Button>
              <Button
                colorScheme="red"
                onClick={() => handleDeleteUser(deletingUser?.id)}
                size="lg"
                minW="120px"
                borderRadius="8px"
                isLoading={actionLoading.delete}
                loadingText="جاري الحذف..."
                _hover={{ transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)" }}
              >
                حذف
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
